/**
 * Unauthenticated "Add update" page for the per-item link in nudge/recap emails
 * (PRD_ACTION_ITEMS_OVERHAUL Phase 2). Reads ?token=, resolves the item via the
 * action-item-action edge function (POST {info:true}), then submits an optional status
 * change (Done / In progress) plus an optional progress comment.
 *
 * No sign-in required - the HMAC-signed token (recipient + workspace + item + action) is the
 * auth surface, verified server-side. Everything lands in action_item_updates, which is what
 * the nudge/recap follow-through sections render.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, AlertCircle, CircleDot, CircleDashed } from 'lucide-react';
import { edgeFnUrl } from '@saas-boilerplate/shared';

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

type ItemInfo = { title: string; status: string; email: string };

async function callActionItem(token: string, body: Record<string, unknown>): Promise<any> {
  const res = await fetch(edgeFnUrl('action-item-action'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(ANON_KEY ? { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` } : {}),
    },
    body: JSON.stringify({ token, ...body }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  dropped: 'Archived',
};

export function ActionItemUpdatePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<ItemInfo | null>(null);
  const [nextStatus, setNextStatus] = useState<'done' | 'in_progress' | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ status: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('This link is missing its token.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = (await callActionItem(token, { info: true })) as ItemInfo;
        if (!cancelled) setItem(info);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this action item.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async () => {
    if (!nextStatus && !comment.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await callActionItem(token, {
        ...(nextStatus ? { status: nextStatus } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      setSubmitted({ status: String(result?.status || nextStatus || item?.status || 'open') });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your update.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !item) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="mb-2 flex items-center gap-2 text-rose-600">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Link expired</CardTitle>
            </div>
            <CardDescription>
              {error} You can also open Upstand and update the item from the action items page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href="/">Open Upstand</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const closed = submitted.status === 'done';
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="mb-2 flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <CardTitle>{closed ? 'Done. Nice work.' : 'Update saved'}</CardTitle>
            </div>
            <CardDescription>
              {closed
                ? `"${item?.title}" is now marked as done. The team will see it in the next meeting email.`
                : `Your update on "${item?.title}" is saved. The team will see it in the next meeting email.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <a href="/">Open Upstand</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardDescription className="uppercase tracking-widest text-xs">Action item update</CardDescription>
          <CardTitle className="leading-snug">{item?.title}</CardTitle>
          <CardDescription>
            Current status: {STATUS_LABEL[item?.status || 'open'] || item?.status}. Updating as {item?.email}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Change status (optional)</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={nextStatus === 'done' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setNextStatus(nextStatus === 'done' ? null : 'done')}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" /> Done
              </Button>
              <Button
                type="button"
                variant={nextStatus === 'in_progress' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setNextStatus(nextStatus === 'in_progress' ? null : 'in_progress')}
              >
                <CircleDot className="mr-2 h-4 w-4" /> In progress
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="update-comment">Progress update (optional)</Label>
            <Textarea
              id="update-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Spoke to the electrician, he's coming on the 19th"
              rows={3}
              maxLength={2000}
            />
          </div>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
          <Button className="w-full" onClick={submit} disabled={submitting || (!nextStatus && !comment.trim())}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CircleDashed className="mr-2 h-4 w-4" />}
            Save update
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            No sign-in needed. Your update is shared with the meeting team.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
